const MAX_SOURCE_SIZE = 10 * 1024 * 1024;
const MAX_PIXELS = 40 * 1000 * 1000;
const MAX_SIDE = 8192;
const MIN_SIDE = 200;
const MAX_OUTPUT_SIZE = 800 * 1024;
const MAX_OUTPUT_SIDE = 2560;

function readAsArrayBuffer(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('无法读取图片文件'));
    reader.readAsArrayBuffer(blob);
  });
}

function detectImageType(bytes) {
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return 'image/jpeg';
  }

  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47 &&
    bytes[4] === 0x0d && bytes[5] === 0x0a && bytes[6] === 0x1a && bytes[7] === 0x0a) {
    return 'image/png';
  }

  const riff = String.fromCharCode(...bytes.slice(0, 4));
  const webp = String.fromCharCode(...bytes.slice(8, 12));
  if (riff === 'RIFF' && webp === 'WEBP') {
    return 'image/webp';
  }

  return '';
}

function normalizeMimeType(type) {
  return type === 'image/jpg' ? 'image/jpeg' : type.toLowerCase();
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('图片内容损坏或无法解析'));
    };
    image.src = objectUrl;
  });
}

function drawAsJpeg(image, scale, quality) {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));

  const context = canvas.getContext('2d');
  if (!context) throw new Error('当前浏览器无法安全处理图片');

  context.fillStyle = '#fff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/jpeg', quality).replace(/^data:image\/jpeg;base64,/, '');
}

function base64ByteLength(base64) {
  return Math.ceil(base64.length * 3 / 4);
}

export async function prepareSafeImage(file) {
  if (!file) throw new Error('请选择图片');
  if (!file.size) throw new Error('不能上传空文件');
  if (file.size > MAX_SOURCE_SIZE) throw new Error('图片不能超过10MB');

  const declaredType = normalizeMimeType(file.type || '');
  if (declaredType && !['image/jpeg', 'image/png', 'image/webp'].includes(declaredType)) {
    throw new Error('仅支持JPG、PNG或WebP图片');
  }

  const header = new Uint8Array(await readAsArrayBuffer(file.slice(0, 12)));
  const detectedType = detectImageType(header);
  if (!detectedType) throw new Error('文件不是有效的JPG、PNG或WebP图片');
  if (declaredType && declaredType !== detectedType) {
    throw new Error('图片类型与文件内容不一致');
  }

  const image = await loadImage(file);
  const width = image.naturalWidth;
  const height = image.naturalHeight;
  if (width < MIN_SIDE || height < MIN_SIDE) throw new Error('图片尺寸过小，请重新拍摄');
  if (width > MAX_SIDE || height > MAX_SIDE || width * height > MAX_PIXELS) {
    throw new Error('图片像素过大，请压缩后上传');
  }

  let scale = Math.min(1, MAX_OUTPUT_SIDE / width, MAX_OUTPUT_SIDE / height);
  while (Math.round(width * scale) >= MIN_SIDE && Math.round(height * scale) >= MIN_SIDE) {
    for (let quality = 0.9; quality >= 0.5; quality -= 0.1) {
      const safeBase64 = drawAsJpeg(image, scale, quality);
      if (base64ByteLength(safeBase64) <= MAX_OUTPUT_SIZE) return safeBase64;
    }
    scale *= 0.8;
  }

  throw new Error('图片处理后仍然过大，请重新拍摄');
}
